import { useTheme } from "@mui/material";
import { CarPortfolioChartItem } from "@app/pages/assetAllocation/asset-allocation-types";
import {
  CarPdfHeader,
  CarPdfHeaderCell,
  CarPdfRow,
  CarPdfTable,
  CarPdfTableProps,
  CarPdfTextCell,
} from "@app/components/PdfTable";
import { CarGicsItem } from "@app/hooks/useGics";
import { DecimalOptions, formatPercentFactor } from "@app/utils";
import { ReactElement } from "react";

interface SectorAnalysisFocusPdfTableProps
  extends Omit<CarPdfTableProps, "children" | "collWidths"> {
  data: CarPortfolioChartItem[];
  sectorFocusItem: CarGicsItem;
  data1Id: string;
  data2Id: string;
}

export const SectorAnalysisFocusPdfTable = ({
  data,
  sectorFocusItem,
  data1Id,
  data2Id,
  ...props
}: SectorAnalysisFocusPdfTableProps) => {
  const theme = useTheme();

  const label1 = data.find((i) => i.id === data1Id)?.label;
  const label2 = data.find((i) => i.id === data2Id)?.label;

  const renderRow = (
    item: CarGicsItem,
    level: number,
    isSummary: boolean,
  ): ReactElement[] => {
    const getBackgroundColor = () => {
      if (isSummary) {
        return theme.palette.gray3;
      }

      switch (level) {
        case 0:
          return theme.palette.white;
        case 1:
          return theme.palette.gray1;
        case 2:
          return theme.palette.gray2;
        case 3:
          return theme.palette.gray3;
        case 4:
          return theme.palette.gray4;
        default:
          return theme.palette.white;
      }
    };

    const decimalOptions: DecimalOptions = {
      decimalPlaces: 1,
      forceShowDecimals: true,
    };

    return [
      <CarPdfRow key={item.id} paddingVertical={8}>
        <CarPdfTextCell
          textVariant={isSummary ? "v12Bold" : undefined}
          style={{
            textAlign: "left",
            paddingHorizontal: 15,
            paddingLeft: 15 + 15 * level,
            backgroundColor: getBackgroundColor(),
          }}
        >
          {item.name}
        </CarPdfTextCell>
        <CarPdfTextCell
          style={{
            backgroundColor: getBackgroundColor(),
          }}
        >
          {formatPercentFactor(item.value1, decimalOptions)}
        </CarPdfTextCell>
        <CarPdfTextCell
          style={{
            backgroundColor: getBackgroundColor(),
          }}
        >
          {formatPercentFactor(item.value2, decimalOptions)}
        </CarPdfTextCell>
        <CarPdfTextCell
          style={{
            backgroundColor: getBackgroundColor(),
          }}
        >
          {formatPercentFactor(item.value1 - item.value2, decimalOptions)}
        </CarPdfTextCell>
      </CarPdfRow>,
      ...item.children.flatMap((i) => renderRow(i, level + 1, false)),
    ];
  };

  return (
    <CarPdfTable colWidths={[3, 1, 1, 1]} isRounded isStriped {...props}>
      <CarPdfHeader paddingVertical={8} key="label">
        <CarPdfHeaderCell></CarPdfHeaderCell>
        <CarPdfHeaderCell>{label1}</CarPdfHeaderCell>
        <CarPdfHeaderCell>{label2}</CarPdfHeaderCell>
        <CarPdfHeaderCell>Difference</CarPdfHeaderCell>
      </CarPdfHeader>
      {sectorFocusItem.children.map((row) => renderRow(row, 0, false))}
      {renderRow(
        {
          id: "total",
          children: [],
          code: "total",
          description: "total",
          name: "Sector Total:",
          value1: sectorFocusItem.value1,
          value2: sectorFocusItem.value2,
          diff: sectorFocusItem.diff,
        },
        0,
        true,
      )}
    </CarPdfTable>
  );
};
